import { useMemo } from 'react';

import {
	formatMoney as formatMoneyAmount,
	localizeMoney as localizeMoneyAmount,
	parseMoney,
	parseLocalizedMoney as parseLocalizedMoneyAmount,
} from '@eventespresso/utils';
import { useConfig } from '../config';

import type { FormatMoneyFunction, ParseLocalizedMoneyFunction, ParseMoneyFunction } from '@eventespresso/utils';
import type { Type as ConfigType } from '@eventespresso/config';

export interface MoneyDisplay {
	/** the currency sign if the currency displays it before the amount (or '') */
	afterAmount: string;
	/** the currency sign if the currency displays it before the amount (or '') */
	beforeAmount: string;
	/** the full currency config object */
	currency: ConfigType.Currency;
	/** function for formatting the amount using the correct number of decimal places for the currency */
	formatMoney: ReturnType<FormatMoneyFunction>;
	/** convert given amount into localized string (decimal mark, thousands separator, decimal places) */
	localizeMoney: ReturnType<FormatMoneyFunction>;
	/** function for unformatting the amount using the correct number of decimal places for the currency */
	parseMoney: ParseMoneyFunction;
	/** convert given localized amount as a string into a float */
	parseLocalizedMoney: ReturnType<ParseLocalizedMoneyFunction>;
}

export const useMoneyDisplay = (): MoneyDisplay => {
	const { currency, locale } = useConfig();

	const afterAmount: MoneyDisplay['afterAmount'] = currency.signB4 ? '' : currency.sign;
	const beforeAmount: MoneyDisplay['beforeAmount'] = currency.signB4 ? currency.sign : '';

	const formatMoney: MoneyDisplay['formatMoney'] = formatMoneyAmount(currency.decimalPlaces);

	const localizeMoney: MoneyDisplay['localizeMoney'] = localizeMoneyAmount({
		locale: locale.site,
		decimalPlaces: currency.decimalPlaces,
		decimalMark: currency.decimalMark,
		thousandsSeparator: currency.thousandsSeparator,
	});

	const parseLocalizedMoney: MoneyDisplay['parseLocalizedMoney'] = parseLocalizedMoneyAmount({
		locale: locale.site,
		decimalMark: currency.decimalMark,
		thousandsSeparator: currency.thousandsSeparator,
	});

	return useMemo(
		() => ({
			afterAmount,
			beforeAmount,
			currency,
			formatMoney,
			localizeMoney,
			parseMoney,
			parseLocalizedMoney,
		}),
		[afterAmount, beforeAmount, currency, localizeMoney, formatMoney, parseLocalizedMoney]
	);
};

export default useMoneyDisplay;
